import { IconCheck } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { PickerRow } from '../lib/model-picker';
import { ModelPickerOffer } from './ModelPickerOffer';

// One option of the `ModelPicker` listbox (#5210, epic #5200): the option element
// around one `PickerRow`. The listbox (`ModelPickerList`) owns the keyboard and which
// row is active; this file paints one row and reports a click.
//
// Focus never lands here: the combobox input keeps it and names the active row with
// `aria-activedescendant` (the WAI-ARIA combobox pattern `CommandPaletteDialog`
// follows), which is why an option carries no tabindex and no key handler.

export type ModelPickerRowProps = {
  readonly row: PickerRow;
  readonly domId: string;
  /** The listbox cursor is on this row. */
  readonly active: boolean;
  /** Absent on a row that cannot be chosen (unavailable, gone). */
  readonly onPick?: () => void;
};

export function ModelPickerRow(props: ModelPickerRowProps): JSX.Element {
  const body = (): JSX.Element => {
    const row = props.row;
    switch (row.kind) {
      case 'automatic':
        return (
          <span class="model-picker__label">
            <span class="model-picker__name">{t('agents.automatic')}</span>
            <span class="model-picker__meta">{t('agents.picker.automatic_hint')}</span>
          </span>
        );
      case 'gone':
        return (
          <span class="model-picker__label">
            <span class="model-picker__name">{row.displayName}</span>
            <code class="model-picker__id">{row.model}</code>
            <span class="model-picker__meta">{t('agents.picker.gone')}</span>
          </span>
        );
      default:
        return <ModelPickerOffer row={row} />;
    }
  };
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: an aria-activedescendant option; focus stays on the combobox input by design.
    // biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard path is the combobox's Enter, not a per-option listener.
    <div
      role="option"
      id={props.domId}
      class="model-picker__option"
      classList={{ 'model-picker__option--active': props.active }}
      data-option={props.row.id}
      aria-selected={props.row.current}
      aria-disabled={props.onPick === undefined}
      // Keep focus on the input: a mousedown would blur it before the click lands.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => props.onPick?.()}
    >
      {body()}
      <Show when={props.row.current}>
        <IconCheck class="model-picker__check" size="1rem" />
      </Show>
    </div>
  );
}
